/**
 * Contains the component for message bundle access.
 * 
 * File: \src\stores\I18nStore.ts
 * Created Date: Monday, March 24th 2025, 13:42:33
 * Author: Daniel Nikolov
 * -----
 * Last Modified: March 24th 2025, 13:43:59
 * Modified By: Daniel Nikolov
 * -----
 * Copyright (c) 2024-2025 Liebherr-Digital Development Center GmbH
 */


import { Ref, nextTick } from 'vue';
import { Composer, NamedValue } from 'vue-i18n';

import { Settings } from 'luxon';
import { defineStore } from 'pinia';

import i18n from '@/main';

import LocalStorage from '@/stores/LocalStorageStore';

export interface Language {
  name: string;
  code: string;
  language: string;
  detail: string;
}

export interface LanguageOption {
  label: string;
  value: string;
  detail: string;
}

/**
 * Defines the store's state.
 *
 * @interface ConfigState
 */
interface I18nState {
  loadedLanguage: Language;
  fallbackLanguage: Language;
  availableLanguages: Language[];
}

// The array of supported languages.
const languages: Language[] = [
  { name: 'English', code: 'en-US', language: 'en', detail: '(US)' }
];

/**
 * This class switches the language in the UI for us.
 * It extends VueI18n for that purpose. Message bundles are
 * assumed to be in the same folder, named '<purpose>_<locale>'.json.
 *
 * @export
 * @class I18nSwitcher
 * @extends {Vue}
 */

export const useI18nStore = defineStore('i18nStore', {
  // The state. Language names are i18n'ed and hardcoded by design!
  state: (): I18nState => ({
    availableLanguages: languages,
    loadedLanguage: languages[0],
    fallbackLanguage: languages[0]
  }),

  getters: {
    /**
     * Getter for the available languages.
     *
     * @return {*}  {Language[]} All available languages.
     */
    getAvailableLanguages(): Language[] {
      return this.availableLanguages;
    },

    /**
     * Getter for the loaded language.
     *
     * @return {*}  {Language} The language.
     */
    getLoadedLanguage(): Language {
      return this.loadedLanguage;
    },

    /**
     * Getter for the loaded language.
     *
     * @return {*}  {string} the IANA code.
     */
    getLoadedLanguageCode(): string {
      return this.loadedLanguage.code;
    },

    /**
     * Getter for the name of the loaded language.
     *
     * @return {*}  {string} The language name, e.g. Español
     */
    getLoadedLanguageName(): string {
      return this.loadedLanguage.name;
    },

    /**
     * Getter for the loaded language.
     *
     * @return {*}  {string} e.g. "en", "de", "fr" or "es"
     */
    getLoadedLanguageLanguage(): string {
      return this.loadedLanguage.language;
    },

    /**
     * CHecks whether a language wil the code is loaded currently.
     *
     * @return {*}  {(code: string) => boolean} Function to check.
     */
    isLanguageLoaded(): (code: string) => boolean {
      return (code: string): boolean => {
        return this.loadedLanguage.code === code;
      };
    },

    /**
     * Getting the checked language from the browser.
     *
     * @return {*}  {Language} The found language with en_US as fallback.
     */
    preferredLanguage(): Language {
      let foundLanguage: Language = this.fallbackLanguage;

      // Check local storage first
      const storedLanguageCode = LocalStorage.getStoredLanguage();
      if (storedLanguageCode) {
        foundLanguage = this.availableLanguages.find((availLanguage) => storedLanguageCode === availLanguage.code);
      }

      // Nothing found, check browser language
      if (!foundLanguage) {
        // Try to match navigator.language to our language code ("en-US", "en-GB", ...) exactly first:
        foundLanguage = this.availableLanguages.find((availLanguage) => navigator.language === availLanguage.code);
      }

      // If not found, look for the language by language ("en", "de", ...):
      if (!foundLanguage) {
        foundLanguage = this.availableLanguages.find((availLanguage) =>
          navigator.language.startsWith(availLanguage.language)
        );
      }

      if (!foundLanguage) {
        // Default: en-US
        foundLanguage = this.fallbackLanguage;
      }

      return foundLanguage;
    }
  },

  actions: {
    /**
     * Access to the standard t method. You can pass a NamedValue instance for named interpolation.
     * This is needed because the original useI18n can only be used in Vue components.
     *
     * @example
     * "t('just.key')"
     * "t('just.key.interpolated', { toreplace: 'sometext' })" with "'just.key.interpolated' = 'Test {toreplace}'"
     *
     * @param {string} key The key to access.
     * @param {NamedValue} [namedValue] The named values to replace/interpolate
     * @return {*}  {string} The string out of the bundle.
     */
    t(key: string, namedValue?: NamedValue): string {
      const composer: Composer = i18n as Composer;
      return composer.t(key, namedValue).toString();
    },

    /**
     * Access to the standard tc method. You can a value to choose the interpolation.
     * This is needed because the original useI18n can only be used in Vue components.
     *
     * @example
     * tc('just.key', 0) with "'just.key' = 'None|One|Multiple'" (result: "None")
     * tc('just.key', 1) with "'just.key' = 'None|One|Multiple'" (result: "One")
     * tc('just.key', 5) with "'just.key' = 'None|One|Multiple'" (result: "Multiple")
     *
     * @return {*}  {string} The string out of the bundle.
     */
    tc(key: string, plural: number): string {
      const composer: Composer = i18n as Composer;
      return composer.t(key, plural).toString();
    },

    /**
     * Inits the store. Uses en-US as the initial default language.
     */
    init(): void {
      const browserLanguage: Language = this.preferredLanguage;

      this.loadLanguageAsync(browserLanguage);
    },

    /**
     * Loads the language asynchronously.
     *
     * @param {Language} language The language to load.
     * @return {*}  {Promise<void>}
     */
    async loadLanguageAsync(language: Language): Promise<void> {
      // load locale messages with dynamic import
      const messages = await import(`../lang/${language.code}.json`);

      // set locale and locale message
      i18n.setLocaleMessage(language.code, messages.default);
      (i18n.locale as Ref<string>).value = language.code;

      document.querySelector('html')?.setAttribute('lang', language.code);

      Settings.defaultLocale = language.code;
      this.loadedLanguage = language;
      LocalStorage.storeLanguage(language.code);

      return nextTick();
    },

    /**
     * Finds a language by code.
     *
     * @param {string} code The language code (e.g. 'de-DE') to find the Language for.
     * @return {*}  {Language} The instance or the fallbackLanguage.
     */
    findLanguageByCode(code: string): Language {
      let foundLanguage = this.availableLanguages.find((language) => language.code === code);

      if (!foundLanguage) {
        foundLanguage = this.fallbackLanguage;
      }

      return foundLanguage;
    }
  }
});
